// Экспорт расписания тура одной группы лиги в PNG: все игры тура с составами,
// у каждого игрока — текущее место в таблице группы и баллы в скобках.
// Ручной рендер на canvas — общие помощники в canvasShared.js.

import {
  COLORS, FONT_FAMILY, ensureFontsReady, canvasToBlob, triggerDownload, ellipsize,
} from '@/views/fnf-league/canvasShared'

const WIDTH = 1080
const PADDING = 48
const HEADER_HEIGHT = 190
const GAME_HEADER_HEIGHT = 72
const ROW_HEIGHT = 72
const GAME_GAP = 32
const BADGE_RADIUS = 22

// Золото / серебро / бронза для бейджей мест в таблице, дальше — приглушённый
const PLACE_BADGE_COLORS = {
  1: '#ffd166',
  2: '#c3cbdc',
  3: '#d8945b',
}

// «20.09 22:00» из ISO-строки бэкенда (уже в МСК) — без new Date(),
// иначе часовой пояс браузера сдвинул бы время
function formatMskDateTime(value) {
  const match = /^\d{4}-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(value || '')
  if (!match) return null
  const [, month, day, hours, minutes] = match

  return `${day}.${month} ${hours}:${minutes}`
}

/**
 * @param {number} season
 * @param {string} groupLetter
 * @param {number} tour
 * @param {Array} games игры тура в группе (с players)
 * @param {Object} standingsByContactId contact_id => строка таблицы группы (place, total_points)
 */
export default async function exportLeagueTourImage({
  season, groupLetter, tour, games, standingsByContactId,
}) {
  await ensureFontsReady()

  const sortedGames = [...games].sort((a, b) => (a.game_number || 0) - (b.game_number || 0))
  const gamesHeight = sortedGames.reduce(
    (sum, game) => sum + GAME_HEADER_HEIGHT + game.players.length * ROW_HEIGHT,
    0,
  ) + GAME_GAP * Math.max(sortedGames.length - 1, 0)
  const height = HEADER_HEIGHT + PADDING + gamesHeight + PADDING

  const canvas = document.createElement('canvas')
  canvas.width = WIDTH
  canvas.height = height
  const ctx = canvas.getContext('2d')

  ctx.fillStyle = COLORS.background
  ctx.fillRect(0, 0, WIDTH, height)

  // Шапка: название лиги, сезон, группа и тур
  ctx.fillStyle = COLORS.cardTop
  ctx.fillRect(0, 0, WIDTH, HEADER_HEIGHT)
  ctx.textBaseline = 'middle'
  ctx.fillStyle = COLORS.headerText
  ctx.font = `bold 52px ${FONT_FAMILY}`
  ctx.fillText('ФНФ ЛИГА', PADDING, 58)
  ctx.font = `bold 34px ${FONT_FAMILY}`
  ctx.fillText(`Сезон ${season} · Группа ${groupLetter} — Тур ${tour}`, PADDING, 116)
  ctx.font = `24px ${FONT_FAMILY}`
  ctx.fillStyle = 'rgba(255, 255, 255, 0.85)'
  ctx.fillText('Игры тура · место в таблице (баллы)', PADDING, 158)

  const nameLeft = PADDING + BADGE_RADIUS * 2 + 28
  const right = WIDTH - PADDING
  let y = HEADER_HEIGHT + PADDING

  sortedGames.forEach(game => {
    // Заголовок игры: номер слева, дата и ведущий справа (если слот назначен)
    ctx.fillStyle = COLORS.rowHighlight
    ctx.fillRect(PADDING / 2, y, WIDTH - PADDING, GAME_HEADER_HEIGHT)
    const headerMiddle = y + GAME_HEADER_HEIGHT / 2
    ctx.font = `bold 32px ${FONT_FAMILY}`
    ctx.fillStyle = COLORS.accent
    ctx.fillText(game.game_number ? `Игра ${game.game_number}` : 'Игра', PADDING, headerMiddle)
    const infoText = [formatMskDateTime(game.scheduled_at), game.host_name ? `ведущий ${game.host_name}` : null]
      .filter(Boolean).join(' · ')
    if (infoText) {
      ctx.font = `26px ${FONT_FAMILY}`
      ctx.fillStyle = COLORS.headerText
      ctx.fillText(infoText, right - ctx.measureText(infoText).width, headerMiddle)
    }
    y += GAME_HEADER_HEIGHT

    // Игроки — по текущему месту в таблице (без строки в таблице — в конец)
    const players = game.players
      .map(player => ({ name: player.name, row: standingsByContactId[player.planfix_contact_id] || null }))
      .sort((a, b) => (a.row?.place ?? Infinity) - (b.row?.place ?? Infinity) || a.name.localeCompare(b.name, 'ru'))

    players.forEach((player, index) => {
      const middle = y + ROW_HEIGHT / 2
      ctx.fillStyle = index % 2 === 0 ? COLORS.rowEven : COLORS.rowOdd
      ctx.fillRect(PADDING / 2, y, WIDTH - PADDING, ROW_HEIGHT)

      // Бейдж текущего места в таблице группы
      const place = player.row?.place ?? null
      ctx.beginPath()
      ctx.arc(PADDING + BADGE_RADIUS, middle, BADGE_RADIUS, 0, Math.PI * 2)
      ctx.fillStyle = PLACE_BADGE_COLORS[place] || COLORS.muted
      ctx.fill()
      ctx.fillStyle = COLORS.background
      ctx.font = `bold 24px ${FONT_FAMILY}`
      const placeText = place === null ? '—' : String(place)
      ctx.fillText(placeText, PADDING + BADGE_RADIUS - ctx.measureText(placeText).width / 2, middle + 1)

      // Баллы в таблице — справа
      ctx.font = `bold 30px ${FONT_FAMILY}`
      ctx.fillStyle = COLORS.muted
      const pointsText = `(${Number(player.row?.total_points ?? 0).toLocaleString('ru-RU')})`
      const pointsWidth = ctx.measureText(pointsText).width
      ctx.fillText(pointsText, right - pointsWidth, middle)

      // Имя — между бейджем и баллами
      ctx.font = `bold 30px ${FONT_FAMILY}`
      ctx.fillStyle = COLORS.text
      ctx.fillText(ellipsize(ctx, player.name, right - pointsWidth - nameLeft - 24), nameLeft, middle)

      y += ROW_HEIGHT
    })

    y += GAME_GAP
  })

  const blob = await canvasToBlob(canvas)
  triggerDownload(blob, `ФНФ_Лига_Сезон${season}_Группа_${groupLetter}_Тур${tour}.png`)
}
